import React, { useCallback, useEffect, useState } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import axios from 'axios';

import { DateField } from '../shared/DateField';

/**
 * CLO Deal Ops — investor-side lifecycle drawer (CLO E2E Phases B+C,
 * 2026-08-29). Ownership boundary: waterfall runs are PROJECTIONS (no cash
 * moves — the trustee runs the real waterfall); actual paydowns/distributions
 * require an evidence reference; equity NAV is OUR valuation of OUR holding.
 */

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.72)', zIndex: 70,
  display: 'flex', justifyContent: 'flex-end',
};
const panel: React.CSSProperties = {
  width: 640, maxWidth: '96vw', height: '100%', overflowY: 'auto',
  background: '#0b1220', borderLeft: '1px solid rgba(148,163,184,0.25)', padding: '18px 22px',
};
const section: React.CSSProperties = {
  border: '1px solid rgba(148,163,184,0.16)', borderRadius: 10,
  padding: '12px 14px', marginBottom: 14, background: 'rgba(15,23,42,0.5)',
};
const label: React.CSSProperties = {
  color: '#64748b', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
};
const h2: React.CSSProperties = { color: '#f8fafc', fontSize: 14, fontWeight: 800, marginBottom: 4 };
const sub: React.CSSProperties = { color: '#64748b', fontSize: 11, marginBottom: 10 };
const input: React.CSSProperties = {
  background: '#0f172a', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 8,
  color: '#e2e8f0', padding: '7px 10px', fontSize: 12, width: '100%',
};
const btn: React.CSSProperties = {
  background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.4)', color: '#60a5fa',
  borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
};
const td: React.CSSProperties = {
  color: '#e2e8f0', fontSize: 12, padding: '4px 8px',
  borderBottom: '1px solid rgba(255,255,255,0.05)', whiteSpace: 'nowrap',
};
const fmt = (v: any, d = 2) => (v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));
const todayIso = () => getTenantToday();

const Row3: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>{children}</div>
);

const CollateralAdder: React.FC<{ dealId: number; onDone: (t: string) => void; onFail: (t: string) => void }> =
  ({ dealId, onDone, onFail }) => {
  const [loanId, setLoanId] = useState('');
  const [par, setPar] = useState('');
  const [mv, setMv] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <input style={{ ...input, width: 110 }} placeholder="Loan id" value={loanId} onChange={e => setLoanId(e.target.value)} />
      <input style={{ ...input, width: 150 }} placeholder="Par $" value={par} onChange={e => setPar(e.target.value)} />
      <input style={{ ...input, width: 150 }} placeholder="MV $ (opt)" value={mv} onChange={e => setMv(e.target.value)} />
      <button style={btn} disabled={busy || !loanId || !par} onClick={async () => {
        setBusy(true);
        try {
          const r = await axios.post(`/api/credit/clo/deals/${dealId}/collateral`, {
            loanId: Number(loanId), parAmount: Number(par), marketValue: mv ? Number(mv) : null,
          });
          onDone(`Collateral ${r.data.action}: ${r.data.borrower} $${Number(r.data.parAmount).toLocaleString()}`);
          setLoanId(''); setPar(''); setMv('');
        } catch (e: any) { onFail(e?.response?.data?.error || e?.message || 'Failed'); }
        finally { setBusy(false); }
      }}>Add</button>
    </div>
  );
};

const CloDealOpsDrawer: React.FC<{ dealId: number | null; dealName?: string; onClose: () => void }> =
  ({ dealId, dealName, onClose }) => {
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  // Waterfall
  const [wfDate, setWfDate] = useState(todayIso());
  const [wfSofr, setWfSofr] = useState('0.053');
  const [wfExpenses, setWfExpenses] = useState('0');
  const [wfPrincipal, setWfPrincipal] = useState('');
  const [wfSteps, setWfSteps] = useState<any[]>([]);
  // NAV
  const [navResult, setNavResult] = useState<any | null>(null);
  const [navHistory, setNavHistory] = useState<any[]>([]);
  // Factor
  const [tranches, setTranches] = useState<any[]>([]);
  const [factorTranche, setFactorTranche] = useState('');
  const [factorAmount, setFactorAmount] = useState('');
  const [factorEvidence, setFactorEvidence] = useState('');
  const [factorDate, setFactorDate] = useState(todayIso());
  // Trustee apply
  const [trusteeReportId, setTrusteeReportId] = useState('');
  // Reinvestment
  const [reinvLoanId, setReinvLoanId] = useState('');
  const [reinvPar, setReinvPar] = useState('');
  const [reinvResult, setReinvResult] = useState<any | null>(null);
  // Repricing + warehouse
  const [repricing, setRepricing] = useState<{ refis: any[]; resets: any[] }>({ refis: [], resets: [] });
  const [warehouse, setWarehouse] = useState<any | null>(null);

  const loadContext = useCallback(async () => {
    if (!dealId) return;
    axios.get(`/api/credit/clo-equity/deals/${dealId}/tranches`)
      .then(r => setTranches(Array.isArray(r.data) ? r.data : (r.data?.tranches || [])))
      .catch(() => setTranches([]));
    axios.get(`/api/credit/clo/deals/${dealId}/equity-nav`)
      .then(r => setNavHistory(Array.isArray(r.data) ? r.data : [])).catch(() => setNavHistory([]));
    Promise.all([
      axios.get(`/api/clo/repricing/deals/${dealId}/refinancings`).catch(() => ({ data: [] })),
      axios.get(`/api/clo/repricing/deals/${dealId}/resets`).catch(() => ({ data: [] })),
    ]).then(([a, b]) => setRepricing({
      refis: Array.isArray(a.data) ? a.data : [], resets: Array.isArray(b.data) ? b.data : [],
    }));
    axios.get(`/api/clo/warehouse/deals/${dealId}/summary`)
      .then(r => setWarehouse(r.data)).catch(() => setWarehouse(null));
  }, [dealId]);

  useEffect(() => { setMsg(null); setWfSteps([]); setNavResult(null); setReinvResult(null); loadContext(); }, [dealId, loadContext]);

  const run = async (fn: () => Promise<any>, okText: (r: any) => string) => {
    setBusy(true); setMsg(null);
    try {
      const r = await fn();
      setMsg({ ok: true, text: okText(r) });
      return r;
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.error || e?.message || 'Failed' });
      return null;
    } finally { setBusy(false); }
  };

  if (!dealId) return null;

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div>
            <div style={{ ...h2, fontSize: 16 }}>Deal Ops — {dealName || `Deal #${dealId}`}</div>
            <div style={sub}>Investor-side lifecycle: projections never move cash; actuals require evidence.</div>
          </div>
          <button style={{ ...btn, background: 'rgba(148,163,184,0.12)', color: '#94a3b8', borderColor: 'rgba(148,163,184,0.3)' }} onClick={onClose}>Close</button>
        </div>

        {msg && (
          <div style={{ ...section, borderColor: msg.ok ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)', color: msg.ok ? '#4ade80' : '#f87171', fontSize: 12 }}>
            {msg.text}
          </div>
        )}

        {/* Waterfall projection */}
        <div style={section}>
          <div style={h2}>Waterfall Projection</div>
          <div style={sub}>Projected priority-of-payments for a payment date. PROJECTION only — actual cash books from trustee evidence.</div>
          <Row3>
            <div><div style={label}>Payment date</div><DateField style={{ width: '100%' }} value={wfDate ?? ''} onChange={iso => setWfDate(iso)} /></div>
            <div><div style={label}>SOFR (decimal)</div><input style={input} value={wfSofr} onChange={e => setWfSofr(e.target.value)} /></div>
            <div><div style={label}>Senior expenses $</div><input style={input} value={wfExpenses} onChange={e => setWfExpenses(e.target.value)} /></div>
          </Row3>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button style={btn} disabled={busy} onClick={() => run(
              () => axios.post(`/api/credit/clo/deals/${dealId}/waterfall/interest`, {
                paymentDate: wfDate, sofrRate: Number(wfSofr), seniorExpenses: Number(wfExpenses) || 0,
              }).then(r => { setWfSteps(r.data?.steps || []); return r.data; }),
              r => `Interest projection: ${r.steps?.length ?? 0} steps, available $${fmt(r.totalAvailable)}`)}>
              Run Interest Projection
            </button>
            <input style={{ ...input, width: 140 }} placeholder="Principal $" value={wfPrincipal} onChange={e => setWfPrincipal(e.target.value)} />
            <button style={btn} disabled={busy || !wfPrincipal} onClick={() => run(
              () => axios.post(`/api/credit/clo/deals/${dealId}/waterfall/principal`, {
                paymentDate: wfDate, principalProceeds: Number(wfPrincipal),
              }).then(r => { setWfSteps(r.data?.steps || []); return r.data; }),
              r => `Principal projection: ${r.steps?.length ?? 0} steps (balances NOT mutated)`)}>
              Run Principal Projection
            </button>
          </div>
          {wfSteps.length > 0 && (
            <div style={{ overflowX: 'auto', maxHeight: 260, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {wfSteps.map((st: any, i: number) => (
                    <tr key={i}>
                      <td style={{ ...td, color: '#64748b' }}>{i + 1}</td>
                      <td style={{ ...td, whiteSpace: 'normal' }}>{st.recipient}</td>
                      <td style={{ ...td, textAlign: 'right' }}>${fmt(st.amountPaid)}</td>
                      <td style={{ ...td, color: st.status === 'PAID' || st.status === 'PAID_FULL' ? '#34d399' : '#fbbf24' }}>{st.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Equity NAV */}
        <div style={section}>
          <div style={h2}>Equity NAV (our valuation)</div>
          <div style={sub}>Quarterly DCF of the equity residual under our assumptions. Persisted as a valuation record.</div>
          <button style={btn} disabled={busy} onClick={() => run(
            () => axios.post(`/api/credit/clo/deals/${dealId}/equity-nav`, {})
              .then(r => { setNavResult(r.data); loadContext(); return r.data; }),
            r => `Equity NAV: fair value $${fmt(r.fairValue)} on invested $${fmt(r.equityInvested)} (IRR ${fmt(Number(r.impliedIRR) * 100, 2)}%)`)}>
            Run Equity NAV (defaults)
          </button>
          {navResult && (
            <div style={{ color: '#e2e8f0', fontSize: 12, marginTop: 8 }}>
              Fair value <b>${fmt(navResult.fairValue)}</b> · invested ${fmt(navResult.equityInvested)} ·
              cash-on-cash {fmt(Number(navResult.cashOnCashYield) * 100, 2)}% · IRR {fmt(Number(navResult.impliedIRR) * 100, 2)}%
            </div>
          )}
          {navHistory.length > 0 && (
            <div style={{ color: '#64748b', fontSize: 11, marginTop: 6 }}>
              {navHistory.length} prior valuation{navHistory.length > 1 ? 's' : ''} · latest {String(navHistory[0]?.valuation_date || '')} @ ${fmt(navHistory[0]?.fair_value)}
            </div>
          )}
        </div>

        {/* Factor paydown */}
        <div style={section}>
          <div style={h2}>Tranche Factor Paydown (evidence-backed)</div>
          <div style={sub}>Reduces the tranche balance and books REPAY trades at par on our held positions. Evidence reference required.</div>
          <Row3>
            <div>
              <div style={label}>Tranche</div>
              <select style={input} value={factorTranche} onChange={e => setFactorTranche(e.target.value)}>
                <option value="">Select…</option>
                {tranches.map((t: any) => (
                  <option key={t.trancheId ?? t.id} value={t.trancheId ?? t.id}>
                    {t.trancheName} ({t.trancheClass ?? t.trancheType ?? '—'}) · ${fmt(t.currentBalance ?? t.originalBalance, 0)}
                  </option>
                ))}
              </select>
            </div>
            <div><div style={label}>Paydown $</div><input style={input} value={factorAmount} onChange={e => setFactorAmount(e.target.value)} /></div>
            <div><div style={label}>As-of</div><DateField style={{ width: '100%' }} value={factorDate ?? ''} onChange={iso => setFactorDate(iso)} /></div>
          </Row3>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={input} placeholder="Evidence ref (e.g. trustee report id / payment-date report)*" value={factorEvidence} onChange={e => setFactorEvidence(e.target.value)} />
            <button style={btn} disabled={busy || !factorTranche || !factorAmount || !factorEvidence} onClick={() => run(
              () => axios.post(`/api/credit/clo/tranches/${factorTranche}/factor`, {
                paydownAmount: Number(factorAmount), asOfDate: factorDate, evidenceRef: factorEvidence,
              }).then(r => { loadContext(); return r.data; }),
              r => `Paydown applied: balance $${fmt(r.oldBalance)} → $${fmt(r.newBalance)} (factor ${fmt(r.factor, 4)}); ${r.heldPositionsPaidDown?.length ?? 0} held position(s) paid down`)}>
              Apply
            </button>
          </div>
        </div>

        {/* Collateral tape */}
        <div style={section}>
          <div style={h2}>Collateral Tape (our copy)</div>
          <div style={sub}>Attach loans to OUR surveillance copy of the deal's tape — derivation input for tests/waterfall/WARF. The trustee's tape is the book of record.</div>
          <CollateralAdder dealId={dealId} onDone={(t) => setMsg({ ok: true, text: t })} onFail={(t) => setMsg({ ok: false, text: t })} />
        </div>

        {/* Trustee apply */}
        <div style={section}>
          <div style={h2}>Apply Trustee Report</div>
          <div style={sub}>Syncs OUR surveillance copy (deal metrics + defaulted-obligor flags) from an ingested report. The trustee remains the book of record.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...input, width: 200 }} placeholder="Trustee report id" value={trusteeReportId} onChange={e => setTrusteeReportId(e.target.value)} />
            <button style={btn} disabled={busy || !trusteeReportId} onClick={() => run(
              () => axios.post(`/api/credit/clo/trustee-reports/${trusteeReportId}/apply`).then(r => r.data),
              r => `Applied report ${r.trusteeReportId}: deal metrics updated; ${r.collateralFlaggedDefaulted} collateral row(s) flagged defaulted`)}>
              Apply to Surveillance
            </button>
          </div>
        </div>

        {/* Reinvestment what-if */}
        <div style={section}>
          <div style={h2}>Reinvestment What-If</div>
          <div style={sub}>Would adding this loan keep the deal's tests in compliance? Analysis only — no gating, no writes.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...input, width: 140 }} placeholder="Loan id" value={reinvLoanId} onChange={e => setReinvLoanId(e.target.value)} />
            <input style={{ ...input, width: 160 }} placeholder="Par amount $" value={reinvPar} onChange={e => setReinvPar(e.target.value)} />
            <button style={btn} disabled={busy || !reinvLoanId || !reinvPar} onClick={() => run(
              () => axios.post(`/api/credit/clo/deals/${dealId}/reinvestment-check`, {
                loanId: Number(reinvLoanId), parAmount: Number(reinvPar),
              }).then(r => { setReinvResult(r.data); return r.data; }),
              r => `What-if: ${r.overallPass === true || r.passed === true ? 'PASSES' : 'CHECK RESULTS'} — see detail below`)}>
              Check
            </button>
          </div>
          {reinvResult && (
            <div style={{ marginTop: 10 }}>
              <div style={{
                display: 'inline-block', padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 800,
                background: reinvResult.compliant ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                color: reinvResult.compliant ? '#4ade80' : '#f87171',
                border: `1px solid ${reinvResult.compliant ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}`,
                marginBottom: 8,
              }}>
                {reinvResult.compliant ? '✓ COMPLIANT — trade would keep all tests passing' : '✗ NOT COMPLIANT — see failing tests'}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Test', 'Pre-trade', 'Post-trade', 'Limit', 'Result'].map(h => (
                      <th key={h} style={{ ...label, textAlign: h === 'Test' ? 'left' : 'right', padding: '4px 8px', borderBottom: '1px solid rgba(148,163,184,0.18)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {([
                    ['WARF (max)', reinvResult.preTradeWarf, reinvResult.postTradeWarf, reinvResult.warfLimit, reinvResult.warfPassed, 0],
                    ['WAS bps (min)', reinvResult.preTradeWas, reinvResult.postTradeWas, reinvResult.wasMinimum, reinvResult.wasPassed, 0],
                    ['Diversity (min)', reinvResult.preTradeDiversity, reinvResult.postTradeDiversity, reinvResult.diversityMinimum, reinvResult.diversityPassed, 0],
                    ['WAL yrs (max)', reinvResult.preTradeWal, reinvResult.postTradeWal, reinvResult.walMaximum, reinvResult.walPassed, 2],
                    ['CCC bucket', null, null, null, reinvResult.cccPassed, 0],
                    ['Concentration', null, null, null, reinvResult.concentrationPassed, 0],
                  ] as Array<[string, any, any, any, boolean, number]>).map(([t, pre, post, lim, ok, d]) => (
                    <tr key={t}>
                      <td style={{ ...td, textAlign: 'left' }}>{t}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{fmt(pre, d)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{fmt(post, d)}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#64748b' }}>{fmt(lim, d)}</td>
                      <td style={{ ...td, textAlign: 'right', color: ok ? '#34d399' : '#f87171', fontWeight: 700 }}>{ok ? 'PASS' : 'FAIL'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {Array.isArray(reinvResult.violations) && reinvResult.violations.length > 0 && (
                <div style={{ color: '#f87171', fontSize: 11, marginTop: 8 }}>
                  {reinvResult.violations.map((v: string, i: number) => <div key={i}>• {v}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Repricing + Warehouse */}
        <div style={section}>
          <div style={h2}>Repricing Events</div>
          {repricing.refis.length === 0 && repricing.resets.length === 0
            ? <div style={sub}>No refinancings or resets recorded for this deal.</div>
            : (
              <div style={{ color: '#e2e8f0', fontSize: 12 }}>
                {repricing.refis.map((r: any, i: number) => <div key={`rf${i}`}>REFI · {r.eventDate || r.event_date} · {r.notes || ''}</div>)}
                {repricing.resets.map((r: any, i: number) => <div key={`rs${i}`}>RESET · {r.eventDate || r.event_date} · {r.notes || ''}</div>)}
              </div>
            )}
        </div>

        <div style={section}>
          <div style={h2}>Warehouse</div>
          {warehouse && (warehouse.facility || warehouse.collateralCount > 0)
            ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {Object.entries(warehouse)
                    .filter(([, v]) => v != null && typeof v !== 'object')
                    .map(([k, v]) => (
                      <tr key={k}>
                        <td style={{ ...td, textAlign: 'left', color: '#64748b' }}>
                          {k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase())}
                        </td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          {typeof v === 'number' ? fmt(v) : String(v)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )
            : <div style={sub}>No warehouse facility on file for this deal.</div>}
        </div>
      </div>
    </div>
  );
};

export default CloDealOpsDrawer;
